import { applyCommand, type Command } from "../document/commands.ts";
import { COLLISION_CELLS_PER_TILE, type CollisionLayer, type MapDocument, type WorldDocument, type ZoneLayer } from "../document/types.ts";
import { zoneValue } from "../document/zones.ts";
import type { ReplaceScope } from "./replace.ts";

/**
 * Replace in selection for collision values and zone types, the pure part (ADR-097's amendment of 2026-10-12,
 * decisions 5 to 11): what an area holds on the collision and zone layers in scope, and the fills that replace
 * the ticked values in it. A collision or zone layer holds `COLLISION_CELLS_PER_TILE` cells a tile each way, so
 * the area is read in collision cells, a third stride beside the tile grid's and the corner grid's. Door
 * layers are never read or written: a door cell holds a door's id, and moving it is a door edit (decision 11).
 */

/**
 * A collision value the area holds, counted in collision cells over every collision layer in scope.
 */
export interface ReplaceCollisionSource {
  readonly kind: "collision";
  readonly value: number;
  readonly count: number;
}

/**
 * A zone type the area holds, by id, counted in collision cells over every zone layer in scope.
 */
export interface ReplaceZoneSource {
  readonly kind: "zone";
  readonly type: string;
  readonly count: number;
}

/**
 * One collision or zone line of the "from" list.
 */
export type ReplaceCellSource = ReplaceCollisionSource | ReplaceZoneSource;

/**
 * What to replace with what: the ticked collision values, all becoming one value, and the ticked zone types,
 * all becoming one type; null for a kind with nothing ticked.
 */
export interface ReplaceCellPicks {
  readonly collision: { readonly from: readonly number[]; readonly to: number } | null;
  readonly zones: { readonly from: readonly string[]; readonly to: string } | null;
}

/**
 * A cell replace ready to run: the compound of its fills, the collision cells and zone cells it changes, and
 * the layers it writes in the map's order.
 */
export interface ReplaceCellPlan {
  readonly command: Extract<Command, { readonly kind: "compound" }>;
  readonly collision: number;
  readonly zones: number;
  readonly layers: readonly string[];
}

/**
 * An area in collision cells, both corners inclusive.
 */
export interface CellBounds {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

const MAX_BYTE = 255;

/**
 * The collision cells a rectangle of tiles holds: columns `4 * x0` to `4 * x1 + 3` and rows likewise, clipped
 * to the map's collision grid, or the whole grid.
 *
 * @param rect The rectangle of tiles, both corners inclusive, or null for the whole grid.
 * @param map The map, whose size gives the grid's.
 * @returns The first and last column and row, inclusive; empty when the last is before the first.
 */
export function cellBoundsOf(rect: ReplaceScope["rect"], map: MapDocument): CellBounds {
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const height = map.height * COLLISION_CELLS_PER_TILE;
  if (rect === null) {
    return { x0: 0, y0: 0, x1: width - 1, y1: height - 1 };
  }
  const last = COLLISION_CELLS_PER_TILE - 1;
  return {
    x0: Math.max(0, rect.x0 * COLLISION_CELLS_PER_TILE),
    y0: Math.max(0, rect.y0 * COLLISION_CELLS_PER_TILE),
    x1: Math.min(width - 1, rect.x1 * COLLISION_CELLS_PER_TILE + last),
    y1: Math.min(height - 1, rect.y1 * COLLISION_CELLS_PER_TILE + last),
  };
}

/**
 * The collision and zone layers in scope, in the map's order.
 *
 * @param map The map.
 * @param layerIds The layers in scope.
 * @returns The collision and zone layers among them.
 */
function scopedCellLayers(map: MapDocument, layerIds: readonly string[]): (CollisionLayer | ZoneLayer)[] {
  const wanted = new Set(layerIds);
  return map.layers.filter((layer): layer is CollisionLayer | ZoneLayer => wanted.has(layer.id) && (layer.kind === "collision" || layer.kind === "zone"));
}

/**
 * The cells of a layer inside an area grouped by the byte each holds, leaving out 0.
 *
 * @param map The map.
 * @param layer The layer.
 * @param rect The area.
 * @returns The indices of each byte held, in row-major order.
 */
function cellsByByte(map: MapDocument, layer: CollisionLayer | ZoneLayer, rect: ReplaceScope["rect"]): Map<number, number[]> {
  const stride = map.width * COLLISION_CELLS_PER_TILE;
  const { x0, y0, x1, y1 } = cellBoundsOf(rect, map);
  const groups = new Map<number, number[]>();
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const index = y * stride + x;
      const held = layer.cells[index] as number;
      if (held === 0) {
        continue;
      }
      const group = groups.get(held);
      if (group === undefined) {
        groups.set(held, [index]);
      } else {
        group.push(index);
      }
    }
  }
  return groups;
}

/**
 * The zone type a zone layer's byte stands for.
 *
 * @param layer The layer.
 * @param held The byte, 1 or more.
 * @returns The type id.
 * @throws Error when the layer's table holds no type for the byte.
 */
function zoneTypeOf(layer: ZoneLayer, held: number): string {
  const type = layer.types[held - 1];
  if (type === undefined) {
    throw new Error(`layer ${layer.id} holds byte ${String(held)}, which its table of ${String(layer.types.length)} types does not name`);
  }
  return type;
}

/**
 * Lists the collision values and zone types an area holds on the layers in scope: each collision value once
 * over every collision layer, then each zone type once over every zone layer, each kind most used first and
 * then by value or by id. A cell holding 0, the value an erase leaves, is not listed.
 *
 * @param map The map.
 * @param scope The area, in tiles, and the layers.
 * @returns The lines; none when the area holds nothing on those layers.
 */
export function scanReplaceCells(map: MapDocument, scope: ReplaceScope): ReplaceCellSource[] {
  const values = new Map<number, number>();
  const types = new Map<string, number>();
  for (const layer of scopedCellLayers(map, scope.layerIds)) {
    for (const [held, indices] of cellsByByte(map, layer, scope.rect)) {
      if (layer.kind === "collision") {
        values.set(held, (values.get(held) ?? 0) + indices.length);
      } else {
        const type = zoneTypeOf(layer, held);
        types.set(type, (types.get(type) ?? 0) + indices.length);
      }
    }
  }
  const collision = [...values.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]).map(([value, count]): ReplaceCollisionSource => ({ kind: "collision", value, count }));
  const zones = [...types.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)).map(([type, count]): ReplaceZoneSource => ({ kind: "zone", type, count }));
  return [...collision, ...zones];
}

/**
 * Checks what the ticked lines become: a collision value is a byte other than 0, a zone type an id.
 *
 * @param picks What to replace with what.
 * @throws Error when a kind becomes nothing or a value it cannot hold.
 */
function checkPicks(picks: ReplaceCellPicks): void {
  const to = picks.collision?.to;
  if (to !== undefined && (!Number.isInteger(to) || to < 1 || to > MAX_BYTE)) {
    throw new Error(`a collision value becomes a byte from 1 to ${String(MAX_BYTE)}, not ${String(to)}`);
  }
  if (picks.zones !== null && zoneValue(picks.zones.to, "a zone replace writes") === null) {
    throw new Error("a zone type becomes a type, not nothing");
  }
}

/**
 * Plans a cell replace as one compound of `fillCells`, one for each layer in scope and each ticked value it
 * holds in the area, in the map's order. Each fill's indices are read from the map as given: a layer's cells
 * grouped by the byte they hold are disjoint, so no fill touches another's cells. Each fill is then applied to
 * the document the one before it left, so a fill the layer cannot take throws here and not when the plan is
 * run; a zone fill names its types by id, which a fill before it on the same layer may have renumbered. A
 * zone layer is left canonical by the fill itself. A ticked value that already is the one chosen adds nothing.
 *
 * @param world The world.
 * @param mapId The map replaced on.
 * @param scope The area, in tiles, and the layers.
 * @param picks What to replace with what.
 * @returns The plan, or null when nothing would change.
 * @throws Error when the map is missing, a kind becomes nothing or a value it cannot hold, or a zone layer
 *   would hold more types than a byte indexes.
 */
export function planReplaceCells(world: WorldDocument, mapId: string, scope: ReplaceScope, picks: ReplaceCellPicks): ReplaceCellPlan | null {
  checkPicks(picks);
  const map = world.maps.find((m) => m.id === mapId);
  if (map === undefined) {
    throw new Error(`map ${mapId} does not exist`);
  }
  const values = new Set(picks.collision?.from ?? []);
  const types = new Set(picks.zones?.from ?? []);
  const commands: Command[] = [];
  const written: string[] = [];
  let current = world;
  let collision = 0;
  let zones = 0;
  for (const layer of scopedCellLayers(map, scope.layerIds)) {
    const before = commands.length;
    for (const [held, indices] of cellsByByte(map, layer, scope.rect)) {
      let command: Command | null = null;
      if (layer.kind === "collision" && picks.collision !== null && values.has(held) && held !== picks.collision.to) {
        command = { kind: "fillCells", mapId, layerId: layer.id, indices: Uint32Array.from(indices), before: held, after: picks.collision.to };
        collision += indices.length;
      } else if (layer.kind === "zone" && picks.zones !== null) {
        const type = zoneTypeOf(layer, held);
        if (types.has(type) && type !== picks.zones.to) {
          command = { kind: "fillCells", mapId, layerId: layer.id, indices: Uint32Array.from(indices), before: type, after: picks.zones.to };
          zones += indices.length;
        }
      }
      if (command !== null) {
        current = applyCommand(current, command);
        commands.push(command);
      }
    }
    if (commands.length > before) {
      written.push(layer.id);
    }
  }
  return commands.length === 0 ? null : { command: { kind: "compound", commands }, collision, zones, layers: written };
}
